// Every sign-in step in App admin's two-column look: the form on the left, the Firstfund panel on the right
// (wide screens only). The kit's teal / charcoal / gold, so it matches the rest of the portal.
import type { ReactNode } from 'react'
import { EyeOff, ShieldCheck, UsersRound, type LucideIcon } from 'lucide-react'
import { FirstfundMark } from '@/components/FirstfundMark'

interface AuthLayoutProps {
  title: string
  subtitle?: string
  children: ReactNode
}

export function AuthLayout({ title, subtitle, children }: AuthLayoutProps) {
  return (
    <div
      className="flex min-h-screen flex-col bg-canvas dark:bg-canvas-dark lg:flex-row"
      data-testid="page-sign-in"
    >
      <div className="flex flex-1 flex-col justify-center p-6 sm:p-8 lg:p-12">
        <div className="mx-auto w-full max-w-md">
          <div className="mb-8 inline-flex items-center gap-2 text-charcoal dark:text-white">
            <FirstfundMark size={30} />
            <div>
              <p className="text-base font-bold leading-none text-default">Campaign portal</p>
              <p className="mt-0.5 text-2xs leading-none text-muted">Firstfund · our team only</p>
            </div>
          </div>
          <h1 className="mb-1 text-2xl font-bold text-default sm:text-3xl">{title}</h1>
          {subtitle && <p className="mb-6 text-xs text-muted">{subtitle}</p>}
          {children}
        </div>
      </div>
      <div className="relative hidden flex-1 overflow-hidden bg-gradient-to-br from-primary via-primary-dark to-charcoal lg:flex">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute left-10 top-10 h-32 w-32 rounded-full bg-white blur-3xl" />
          <div className="absolute right-1/3 top-1/2 h-24 w-24 rounded-full bg-accent blur-2xl" />
        </div>
        <div className="relative flex flex-col justify-center px-12 text-white xl:px-20">
          <p className="mb-3 text-2xs font-bold uppercase tracking-wider text-white/70">
            Firstfund campaign portal
          </p>
          <h2 className="mb-3 text-3xl font-bold leading-tight xl:text-4xl">
            The right people, the right channel, inside the rules.
          </h2>
          <p className="mb-8 max-w-md text-sm text-white/80">
            IVR, WhatsApp and SMS campaigns for our leads: audiences after the suppression list, workflows,
            and sends spread across our numbers under their daily limits.
          </p>
          <div className="space-y-4">
            <Highlight
              icon={ShieldCheck}
              title="2-step sign-in"
              body="Once you turn it on, a code from your authenticator app after your password."
            />
            <Highlight
              icon={EyeOff}
              title="No personal data on screen"
              body="Lead codes and masked mobiles only; the full mobile is fetched only at send time."
            />
            <Highlight
              icon={UsersRound}
              title="Two people for every campaign"
              body="One person makes it, a second approves it before it can start."
            />
          </div>
        </div>
      </div>
    </div>
  )
}

function Highlight({ icon: Icon, title, body }: { icon: LucideIcon; title: string; body: string }) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/15">
        <Icon size={14} />
      </div>
      <div>
        <p className="mb-0.5 text-sm font-semibold">{title}</p>
        <p className="text-2xs text-white/75">{body}</p>
      </div>
    </div>
  )
}
